import { useEffect, useState } from 'react';

import type { ValidationState } from '@ui-component-library/types/status';

import { Icon } from '@ui-component-library/components/Icon';
import { Tooltip } from '@ui-component-library/components/Tooltip';

import type { DropdownProps } from './Dropdown.types';

import { S } from './Dropdown.styles';

export const Dropdown = ({
  name = 'example_dropdown',
  label,
  description,
  options = [
    { label: 'None', value: 'none' },
    { label: 'Option 1', value: 'option1' },
    { label: 'Option 2', value: 'option2' },
    { label: 'Option 3', value: 'option3' }
  ],

  variant = 'default',
  disabled = false,
  value,
  placeholder,
  required = false,

  idleText,
  errorText,
  successText,

  onChange = () => { },
  onFocus = () => { },
  isValid,
  tabIndex = 0
}: DropdownProps) => {
  const [validationState, setValidationState] = useState<ValidationState>('idle');
  const [isFocused, setIsFocused] = useState(false);
  const helperTexts = {
    idle: idleText,
    error: errorText,
    success: successText
  };

  const isFluid = variant === 'fluid';

  useEffect(() => {
    if (isValid !== undefined) {
      setValidationState(isValid ? 'success' : 'error');
      return;
    }
  }, [value, name, onChange, isValid]);

  const handleFocus = () => {
    setIsFocused(true);
    onFocus();
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = e.target.value;
    const selectedOption = options.find((opt) => opt.value === selected);
    const isOptionDisabled = selectedOption?.disabled === true || selectedOption?.loading === true;
    if (isOptionDisabled) {
      return;
    }

    const isSelectedValid = selected !== '';
    setValidationState(isSelectedValid ? 'success' : 'error');

    onChange(selected, isSelectedValid, name);
  };

  const shouldShowHelperText = () => {
    if (disabled) {
      return false;
    }

    return validationState === 'error' || (isFocused && (errorText || idleText || successText));
  };

  return (
    <S.Wrapper variant={variant} disabled={disabled || false}>
      {!isFluid && (
        <S.TopSection>
          <S.Label htmlFor={name}>
            {label}
            {required && <>*</>}
          </S.Label>
          {!disabled && <S.Description>{description}</S.Description>}
        </S.TopSection>
      )}
      <S.SelectContainer $state={disabled ? 'disabled' : validationState}>
        {isFluid && (
          <S.FluidLabel htmlFor={name}>
            <Tooltip content={description} aria-label={`More information about ${label}`}>
              {label}
              {required && <>*</>}

              <S.FluidLabelIcon>
                <Icon name="InfoFilled" size="small" color="charcoal-500" aria-hidden="true" />
              </S.FluidLabelIcon>
            </Tooltip>
          </S.FluidLabel>
        )}

        <S.Select
          name={name}
          id={name}
          $isFluid={isFluid}
          tabIndex={tabIndex}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          value={value || ''}
          disabled={disabled}
        >
          <option value="" disabled hidden>
            {placeholder}
          </option>
          {options.map((option) => {
            const isDisabled = option.disabled === true || option.loading === true;
            const label = option.loading ? `${option.label} (laddar...)` : option.label;
            return (
              <option key={option.value || 'placeholder'} value={option.value} disabled={isDisabled}>
                {label}
              </option>
            );
          })}
        </S.Select>

        <S.IconWrapper>
          <Icon name="ChevronDownSimple" />
        </S.IconWrapper>
      </S.SelectContainer>
      {shouldShowHelperText() && (
        <S.BottomSection>
          <S.HelperText $validationState={validationState}>
            {helperTexts[validationState]}
          </S.HelperText>
        </S.BottomSection>
      )}
    </S.Wrapper>
  );
};
